import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { RemoteSourceGetResponse } from 'api/actions/remote-source-get/remote-source-get-response';
import { useApi } from 'api/api-context';
import RemoteSourceForm, { RemoteSourceFormData } from 'components/forms/remote-source/RemoteSourceForm';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { CONFIG_REMOTE_SOURCE_PAGE_PATH, REMOTE_SOURCES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Allows the user to copy an existing remote source, preserving its configuration.
 */
function CopyRemoteSourcePageImpl({ remoteSourceId }: { remoteSourceId: number }) {
  const navigate = useNavigate();
  const { getAction } = useApi();
  const [remoteSource, setRemoteSource] = useState<RemoteSourceGetResponse | null>(null);

  const initialValues: RemoteSourceFormData | undefined = useMemo(() => {
    if (!remoteSource) {
      return undefined;
    }

    return {
      name: `${remoteSource.name} (Kópia)`,
      type: remoteSource.type,
      url: remoteSource.url ?? '',
      login: remoteSource.login ?? '',
      password: remoteSource.password ?? '',
      incomingApiKey: remoteSource.incomingApiKey ?? '',
      userId: remoteSource.user ? String(remoteSource.user.userId) : '',
    } satisfies RemoteSourceFormData;
  }, [remoteSource]);

  const onSubmit = useCallback(
    async ({ name, type, url, login, password, incomingApiKey, userId }: RemoteSourceFormData) => {
      try {
        const remoteSourceCreateAction = getAction('RemoteSourceCreate');

        await remoteSourceCreateAction({
          payload: {
            name,
            type,
            url,
            login,
            password,
            incomingApiKey,
            userId: userId ? Number(userId) : undefined,
            config: remoteSource?.config,
          },
        });

        notifications.show({
          title: 'Externý zdroj bol vytvorený',
          message: `Externý zdroj ${name} bol úspešne vytvorený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        navigate(CONFIG_REMOTE_SOURCE_PAGE_PATH.insert({ remoteSourceId }));
      } catch (error) {
        panic(error as Error);
      }
    },
    [getAction, navigate, remoteSource, remoteSourceId]
  );

  useEffect(() => {
    const getRemoteSource = getAction('RemoteSourceGet');

    getRemoteSource({ parameters: { remoteSourceId: String(remoteSourceId) } })
      .then(setRemoteSource)
      .catch(panic);
  }, [getAction, remoteSourceId]);

  return (
    <DashboardLayout
      title="Kopírovanie externého zdroja"
      breadcrumbs={[
        { title: 'Externé zdroje', link: REMOTE_SOURCES_PAGE_PATH.original },
        { title: 'Kopírovanie externého zdroja' },
      ]}
    >
      {!!remoteSource && (
        <RemoteSourceForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          primaryButtonText="Vytvoriť"
          primaryIcon={<IconPlus stroke="1.5" height={24} width={24} />}
        />
      )}
    </DashboardLayout>
  );
}

/**
 * Page wrapper responsible for validating the route parameter and rendering the copy page.
 */
export default function CopyRemoteSourcePage() {
  const { remoteSourceId: remoteSourceIdRaw } = useParams();
  const remoteSourceId = useMemo(() => Number(remoteSourceIdRaw), [remoteSourceIdRaw]);

  if (isNaN(remoteSourceId)) {
    return <Navigate to={REMOTE_SOURCES_PAGE_PATH.original} replace />;
  }

  return <CopyRemoteSourcePageImpl remoteSourceId={remoteSourceId} />;
}
